/* Source: /app/utils/syntax_highlighting.cl.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { StreamLanguage, HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags, Tag } from "@lezer/highlight";
import { EditorView } from "@codemirror/view";
let arrowTag = Tag.define();
let controlFlowTag = Tag.define();
let functionNameTag = Tag.define();
let KEYWORDS = ["node", "edge", "walker", "can", "with", "entry", "exit", "def", "class", "obj", "enum", "has", "ability", "visit", "disengage", "yield", "try", "except", "finally", "assert", "include", "as", "global", "async", "await", "lambda", "here", "self", "root", "super", "init", "postinit", "visitor", "impl", "and", "or", "not", "in", "is", "True", "False", "None", "pass", "del", "raise", "test", "check", "glob"];
let CONTROL_FLOW = ["if", "else", "elif", "for", "while", "return", "break", "continue", "spawn", "from", "import"];
let TYPES = ["str", "int", "float", "bool", "list", "dict", "tuple", "set", "any", "type"];
let RE_NUMBER = Reflect.construct(RegExp, ["^(0[xX][0-9a-fA-F]+|0[oO][0-7]+|0[bB][01]+|[0-9]+([.][0-9]+)?([eE][+-]?[0-9]+)?)"]);
let RE_SINGLE_OP = Reflect.construct(RegExp, ["^[=+*/%<>!&|^~-]"]);
let RE_IDENTIFIER = Reflect.construct(RegExp, ["^[a-zA-Z_][a-zA-Z0-9_]*"]);
let RE_PUNCTUATION = Reflect.construct(RegExp, ["^[.:;,@?]"]);
let BACKSLASH = String.fromCharCode(92);
let JAC_TOKEN_TABLE = {"comment": tags.comment, "string": tags.string, "number": tags.number, "keyword": tags.keyword, "controlFlow": controlFlowTag, "type": tags.typeName, "className": tags.className, "function": functionNameTag, "variable": tags.variableName, "operator": tags.operator, "arrow": arrowTag, "punctuation": tags.punctuation};
let jacHighlightStyle = syntaxHighlighting(HighlightStyle.define([{"tag": tags.comment, "color": "#6a9955", "fontStyle": "italic"}, {"tag": tags.string, "color": "#ce9178"}, {"tag": tags.number, "color": "#b5cea8"}, {"tag": tags.keyword, "color": "#569cd6"}, {"tag": controlFlowTag, "color": "#c678dd"}, {"tag": tags.typeName, "color": "#3ac9b0"}, {"tag": tags.className, "color": "#4ec9b0"}, {"tag": functionNameTag, "color": "#dcdcaa"}, {"tag": tags.variableName, "color": "#9cdcfe"}, {"tag": tags.operator, "color": "#d4d4d4"}, {"tag": tags.punctuation, "color": "#d4d4d4"}, {"tag": arrowTag, "color": "#ff6b6b", "fontWeight": "bold"}]));
let jacHighlightStyleLight = syntaxHighlighting(HighlightStyle.define([{"tag": tags.comment, "color": "#008000", "fontStyle": "italic"}, {"tag": tags.string, "color": "#a31515"}, {"tag": tags.number, "color": "#098658"}, {"tag": tags.keyword, "color": "#0000ff"}, {"tag": controlFlowTag, "color": "#af00db"}, {"tag": tags.typeName, "color": "#267f99"}, {"tag": tags.className, "color": "#267f99"}, {"tag": functionNameTag, "color": "#795e26"}, {"tag": tags.variableName, "color": "#001080"}, {"tag": tags.operator, "color": "#24292f"}, {"tag": tags.punctuation, "color": "#24292f"}, {"tag": arrowTag, "color": "#c2410c", "fontWeight": "bold"}]));
function tokenJac(stream, state) {
  if (state.inBlockComment) {
    if (stream.skipTo("*#")) {
      stream.next();
      stream.next();
      state.inBlockComment = false;
    } else {
      stream.skipToEnd();
    }
    return "comment";
  }
  if (stream.match("#*")) {
    if (stream.skipTo("*#")) {
      stream.next();
      stream.next();
    } else {
      stream.skipToEnd();
      state.inBlockComment = true;
    }
    return "comment";
  }
  if (stream.match("#")) {
    stream.skipToEnd();
    return "comment";
  }
  let ch = stream.peek();
  if (((ch === "\"") || (ch === "'"))) {
    let quote = ch;
    stream.next();
    while (!stream.eol()) {
      let c = stream.next();
      if ((c === BACKSLASH)) {
        if (!stream.eol()) {
          stream.next();
        }
      } else if ((c === quote)) {
        return "string";
      }
    }
    return "string";
  }
  if (stream.match(RE_NUMBER)) {
    return "number";
  }
  if ((((stream.match("-->") || stream.match("<--")) || stream.match("++>")) || stream.match("<++"))) {
    return "arrow";
  }
  if ((stream.match("->") || stream.match("<-"))) {
    return "arrow";
  }
  if (((((((((((stream.match("==") || stream.match("!=")) || stream.match("<=")) || stream.match(">=")) || stream.match("+=")) || stream.match("-=")) || stream.match("*=")) || stream.match("/=")) || stream.match("<<")) || stream.match(">>")) || stream.match("**"))) {
    return "operator";
  }
  if (stream.match(RE_SINGLE_OP)) {
    return "operator";
  }
  if (((((((ch === "[") || (ch === "]")) || (ch === "{")) || (ch === "}")) || (ch === "(")) || (ch === ")"))) {
    stream.next();
    return "punctuation";
  }
  let m = stream.match(RE_IDENTIFIER);
  if (m) {
    let word = m[0];
    if (CONTROL_FLOW.includes(word)) {
      return "controlFlow";
    }
    if (KEYWORDS.includes(word)) {
      return "keyword";
    }
    if (TYPES.includes(word)) {
      return "type";
    }
    if (((word[0] >= "A") && (word[0] <= "Z"))) {
      return "className";
    }
    if ((stream.peek() === "(")) {
      return "function";
    }
    return "variable";
  }
  if (stream.match(RE_PUNCTUATION)) {
    return "punctuation";
  }
  stream.next();
  return null;
}
function getJacExtensions(theme = "dark") {
  let isLight = (theme === "light");
  let highlight = (isLight ? jacHighlightStyleLight : jacHighlightStyle);
  let ampStyle = {"background": "transparent", "outline": "none", "fontFamily": "Monaco, Menlo, Ubuntu Mono, monospace"};
  if (isLight) {
    ampStyle["color"] = "#24292f";
  }
  return [StreamLanguage.define({"name": "jac", "startState": () => {
    return {"inBlockComment": false};
  }, "token": tokenJac, "tokenTable": JAC_TOKEN_TABLE}), highlight, EditorView.baseTheme({"&": ampStyle, "&.cm-focused": {"outline": "none"}, ".cm-scroller": {"overflow": "auto"}, ".cm-content": {"padding": "0", "caretColor": "transparent"}}), EditorView.editable.of(false)];
}
export {getJacExtensions};
//# sourceMappingURL=syntax_highlighting.js.map
